import { useLayoutEffect, useRef } from 'react';

// ============================================================
// useViewportFill — make an element end where the screen ends.
//
// Two screens need this: the conversation (MessageThread) and the embedded
// admin consoles (ConsoleFrame). Both are "the whole screen from here down",
// and neither can say so in static CSS, because how far down they start
// depends on the shell around them — straight under the viewport top in the
// owner app, under a console header and a tab bar in the admin panel.
//
// So it measures. The element gets two custom properties:
//
//   --fill-top     how far below the top of the viewport it begins
//   --fill-bottom  the fixed bottom bar it must not hide behind
//
// and the stylesheet does `height: calc(100dvh - var(--fill-top) - var(--fill-bottom))`.
// ============================================================

// Only a bar along the FLOOR of the screen counts. At 768px and up the app's
// bottom nav becomes a 200px-wide, full-height left rail (src/index.css);
// subtracting its height there would be subtracting the whole viewport.
function bottomBarHeight(): number {
  const bar = document.querySelector('.bottom-nav, .pt-bottom-nav');
  if (!bar) return 0;
  const r = bar.getBoundingClientRect();
  const onTheFloor = Math.abs(window.innerHeight - r.bottom) < 4;
  const barShaped = r.height > 0 && r.height <= 200;
  return onTheFloor && barShaped ? Math.round(r.height) : 0;
}

export function useViewportFill<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY);
      el.style.setProperty('--fill-top', `${Math.round(top)}px`);
      el.style.setProperty('--fill-bottom', `${bottomBarHeight()}px`);
    };

    // Opening one of these screens puts you at the top of it, which is also
    // what makes the measurement above mean what it says.
    window.scrollTo({ top: 0 });
    measure();

    // Measure again as the screen settles. Chrome ABOVE the element can go
    // away in the same frame it appears in — the admin console pulls its tab
    // bar once a conversation is open — and a height measured against chrome
    // on its way out leaves the element short of the bottom of the screen.
    const timers = [1, 2, 10, 30].map((n) => window.setTimeout(measure, n * 16));

    window.addEventListener('resize', measure);
    // Keeps up with anything slower: orientation, the on-screen keyboard, a
    // banner appearing above.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(document.body);
      if (el.parentElement) ro.observe(el.parentElement);
    }

    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);

  return ref;
}
